import Vue from 'vue';
import { Pagination } from '@/types';
import { ItemId, BaseItem, EndpointStrategy, Endpoint } from './base';
import { AsyncModule } from 'vuex-async-mutations';

interface PaginatedState {
  itemMap: Record<string, Record<number, Pagination<string>>>;
}

const stateFactory = () => ({
  itemMap: {},
});

export const module = <T extends BaseItem>(
  endpoint: string | EndpointStrategy,
  idProp: ItemId = 'id',
) =>
  ({
    state: stateFactory,

    mutations: {
      ['reset'](state) {
        Object.assign(state, stateFactory());
      },
    },

    getters: {
      ['get:total'](state) {
        return (relationId: string = '*') => state.itemMap[relationId]?.[0]?.meta.total ?? 0;
      },

      ['page'](state, getters) {
        return (page: number = 1, relationId: string = '*'): Pagination<T> | null => {
          if (state.itemMap[relationId] && state.itemMap[relationId][page]) {
            return {
              meta: state.itemMap[relationId][page].meta,
              data: state.itemMap[relationId][page].data
                .filter((id, index, items) => items.indexOf(id) === index)
                .map(id => getters.details(id))
                .filter(node => !!node),
            };
          }

          return null;
        };
      },
    },

    actions: {
      ['reset']: {
        root: true,
        handler({ commit }) {
          commit('reset');
        },
      },

      ['fetch:all']({ dispatch }) {
        return dispatch('fetch:all:page', { perPage: 1000 });
      },
    },

    actionsAsync: {
      ['fetch:all:page']: {
        async handler(
          { commit, commitAsync },
          {
            page = 1,
            relationId,
            query,
            perPage = 25,
            params,
          }: {
            page?: number;
            relationId?: string;
            perPage?: number;
            query?: string;
            params?: Record<string, any>;
          } = {},
        ) {
          let url: Endpoint;

          if (typeof endpoint !== 'string') {
            url = endpoint(relationId);
          } else {
            url = { endpoint };
          }

          const payload = this.$axios.$get<Pagination<T>>(`${url.endpoint}`, {
            params: {
              page,
              query: query === null || query === '' ? undefined : query,
              perPage,
              ...params,
              ...url.params,
            },
          });

          const items: Pagination<T> = await commitAsync('fetch:all:page', payload, {
            page,
            relationId,
          });

          items.data.forEach(item => commit('item:update', item));

          return items;
        },

        resolved(
          state,
          items: Pagination<T>,
          { page, relationId = '*' }: { page: number; relationId?: string },
        ) {
          if (page === 1 || !state.itemMap[relationId]) {
            Vue.set(state.itemMap, relationId, {});
          }

          Vue.set(state.itemMap[relationId], page, {
            meta: items.meta,
            updatedAt: Date.now(),
            data: items.data.map(item => item[idProp]),
          });
        },
      },
    },
  } as AsyncModule<PaginatedState, any>);

export default module;
